import { useCallback, useState } from 'react'
import { readKakaoRestKey } from './kakaoKey'
import { ENDPOINT, KEYWORD_ENDPOINT, PAGE_SIZE, REGION_ENDPOINT } from './kakaoLocal'

/**
 * 주소 검색 — **카카오 로컬 REST API**. FE repo
 * `src/features/problem/composables/useKakaoAddressSearch.ts`의 사본이다(shared 모듈을 만들지
 * 않는다 — FE와 이 repo는 REST contract만 공유한다). 고칠 일이 생기면 양쪽을 함께 고친다.
 *
 * 우편번호 서비스를 이것으로 갈았다(사용자 결정 2026-08-25). 바뀐 이유는 **행정동을
 * 추정하지 않게 되기 때문**이다: 우편번호 서비스는 법정동코드만 주어 표로 행정동을 맞혀야
 * 했고 그 표는 1:N이라 틀릴 수 있었다. 로컬 API는 `h_code`(행정동코드)를 그대로 준다 —
 * 우리 `GET /api/regions`의 `code`와 같은 체계다(2026-08-25 실측: 「경기 광명시 하안로 12」
 * → `h_code 4121064000`(소하1동) · `b_code 4121010400`).
 *
 * **키는 브라우저에 실린다**(사용자 결정). `.env`에는 base64(솔트+키)로 두고 되돌린다 —
 * 난독화이지 암호화가 아니다. 실질적인 방어선은 카카오 콘솔의 도메인 제한이다.
 *
 * **좌표(x·y)는 지도 미리보기에만 쓴다** — 본문에 실어 보내지도, 어디에 저장하지도 않는다.
 *
 * 주소와 장소를 **한 번의 검색에 같이 쏘고 합친다**(2026-08-27). 주소 검색만으로는 장소명을
 * 못 찾는다(실측: 「광명동굴」 → 주소 0건 · 장소 25건). 순서는 주소가 위, 장소가 아래다 —
 * 주소는 좁게(「하안로」 3건), 장소는 넓게(1521건) 잡히므로 좁은 쪽을 위에 둔다.
 * **장소 결과에는 `h_code`·`b_code`·우편번호가 없어**(실측) 고를 때 `resolve`가 좌표로 되묻는다.
 */

/** 카카오 응답 중 **우리가 쓰는 것만** 적는다. */
interface KakaoAddressDocument {
  address_name: string
  address: {
    address_name: string
    /** 행정동코드 10자리 — 우리 `regionCode`다. */
    h_code: string
    /** 법정동코드 10자리. */
    b_code: string
    /** 행정동 이름. 예: `소하1동`. */
    region_3depth_h_name: string
    /** 법정동 이름. 예: `소하동`. 행정동과 다를 수 있다(가학동 ↔ 학온동). */
    region_3depth_name: string
  } | null
  /** 도로명주소. **없는 지점이 있다**(지번만 있는 곳) — 그때 우편번호도 없다. */
  road_address: { address_name: string; zone_no: string } | null
  /** 경도·위도. 문자열로 온다. 지도 미리보기에만 쓴다. */
  x: string
  y: string
}

/** 장소 응답 중 우리가 쓰는 것만. `h_code`·`b_code`는 이 응답에 없다. */
interface KakaoPlaceDocument {
  id: string
  place_name: string
  address_name: string
  /** 도로명 주소. **없는 장소가 있다**(빈 문자열 — 실측: 광명동굴). */
  road_address_name: string
  x: string
  y: string
}

/** 화면이 쓰는 한 줄. */
export interface AddressCandidate {
  kind: 'address' | 'place'
  /**
   * 목록에서 줄을 가르는 값. **장소는 카카오 id를 쓴다** — 같은 지번에 여러 장소가 있어
   * 주소 문자열로는 유일하지 않다(실측: 「가학동 산 7」에 광명동굴 시설 셋).
   */
  id?: string
  /** 장소명 — `kind === 'place'`일 때만 있다. */
  placeName?: string
  /** 입력칸·본문에 남길 주소 글자. 도로명이 있으면 그것을 쓴다. */
  text: string
  /** 지번 주소 — 도로명과 함께 보여 어느 곳인지 가늠하게 한다. */
  jibun: string
  regionCode: string
  regionName: string
  legalCode: string
  legalName: string
  /** 우편번호. **없는 경우가 있다** — 지번만 있는 지점·장소 결과에는 존재하지 않는다. */
  postalCode?: string
  /** 지도 미리보기용 좌표. 보내지도 저장하지도 않는다. */
  lat: number
  lng: number
}

function fromAddress(doc: KakaoAddressDocument): AddressCandidate | null {
  const a = doc.address
  // 주소 객체 자체가 없으면 쓸 수 없다. `h_code`가 비어 있는 것은 고를 때 좌표로 채운다.
  if (a === null) return null
  const zone = doc.road_address?.zone_no?.trim() ?? ''
  return {
    kind: 'address',
    text: doc.road_address?.address_name ?? doc.address_name,
    jibun: a.address_name,
    regionCode: a.h_code,
    regionName: a.region_3depth_h_name,
    legalCode: a.b_code,
    legalName: a.region_3depth_name,
    // 빈 문자열을 담지 않는다 — 없으면 키 자체가 없어야 본문에서도 빠진다.
    ...(zone.length > 0 ? { postalCode: zone } : {}),
    lat: Number(doc.y),
    lng: Number(doc.x),
  }
}

/** 장소 한 줄. **코드가 빈 채로 만든다** — 고를 때 `resolve`가 좌표로 채운다. */
function fromPlace(doc: KakaoPlaceDocument): AddressCandidate {
  const road = doc.road_address_name.trim()
  return {
    kind: 'place',
    id: doc.id,
    placeName: doc.place_name,
    text: road.length > 0 ? road : doc.address_name,
    jibun: doc.address_name,
    regionCode: '',
    regionName: '',
    legalCode: '',
    legalName: '',
    lat: Number(doc.y),
    lng: Number(doc.x),
  }
}

/**
 * 「행정동 소하1동 · 법정동 소하동 · 우편번호 14322」 한 줄(문구·형식은 사용자 결정 2026-08-27).
 * **값이 없는 항목은 빼고 잇는다** — 전부 없으면 빈 문자열이라 부르는 쪽이 줄을 그리지 않는다.
 */
export function addressMetaText(
  adminName: string,
  legalName: string,
  postalCode?: string,
): string {
  return [
    adminName.length > 0 ? `행정동 ${adminName}` : '',
    legalName.length > 0 ? `법정동 ${legalName}` : '',
    postalCode !== undefined && postalCode.length > 0 ? `우편번호 ${postalCode}` : '',
  ]
    .filter((part) => part.length > 0)
    .join(' · ')
}

export interface KakaoAddressSearch {
  results: AddressCandidate[]
  isLoading: boolean
  /** 사람에게 그대로 보여 줄 한 문장. 없으면 `null`. */
  error: string | null
  /** 한 번이라도 찾아봤는가 — 「결과 없음」과 「아직 안 찾음」을 가른다. */
  hasSearched: boolean
  search: (query: string) => Promise<void>
  /**
   * 고른 한 줄을 **쓸 수 있는 값으로 확정한다.** `regionCode`가 이미 있으면 그대로,
   * 비어 있으면(장소·동 이름 검색) 좌표로 행정동을 되물어 채운다.
   */
  resolve: (c: AddressCandidate) => Promise<AddressCandidate>
  /** 목록을 지운다(고른 뒤·입력을 고친 뒤). */
  clear: () => void
}

export function useKakaoAddressSearch(): KakaoAddressSearch {
  const [results, setResults] = useState<AddressCandidate[]>([])
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)

  const clear = useCallback(() => {
    setResults([])
    setError(null)
    setHasSearched(false)
  }, [])

  const search = useCallback(async (query: string) => {
    const q = query.trim()
    if (q.length === 0) return

    const key = readKakaoRestKey()
    if (key === null) {
      setError('주소 검색을 쓸 수 없습니다. 아래에서 동을 직접 골라 주세요.')
      setResults([])
      setHasSearched(true)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const headers = { Authorization: `KakaoAK ${key}` }
      const qs = `query=${encodeURIComponent(q)}&size=${PAGE_SIZE}`
      /*
        **둘을 같이 쏘고 하나가 죽어도 나머지로 간다.** `allSettled`인 이유는 장소 검색이
        실패했다고 주소 결과까지 버릴 이유가 없기 때문이다(그 반대도 같다).
      */
      const [addr, place] = await Promise.allSettled([
        fetch(`${ENDPOINT}?${qs}`, { headers }),
        fetch(`${KEYWORD_ENDPOINT}?${qs}`, { headers }),
      ])

      const rows: AddressCandidate[] = []

      if (addr.status === 'fulfilled' && addr.value.ok) {
        const body = (await addr.value.json()) as { documents?: KakaoAddressDocument[] }
        for (const doc of body.documents ?? []) {
          const row = fromAddress(doc)
          if (row !== null) rows.push(row)
        }
      }

      if (place.status === 'fulfilled' && place.value.ok) {
        const body = (await place.value.json()) as { documents?: KakaoPlaceDocument[] }
        // 같은 자리가 주소·장소 양쪽에 잡히면 한 번만 보여 준다. 지번 문자열로 견준다 —
        // 좌표는 두 API가 소수점까지 같지 않다.
        const seen = new Set(rows.map((r) => r.jibun))
        for (const doc of body.documents ?? []) {
          const row = fromPlace(doc)
          if (seen.has(row.jibun)) continue
          rows.push(row)
        }
      }

      // 둘 다 실패했을 때만 오류다 — 한쪽이라도 답했으면 그 결과로 간다.
      if (
        rows.length === 0 &&
        !(addr.status === 'fulfilled' && addr.value.ok) &&
        !(place.status === 'fulfilled' && place.value.ok)
      ) {
        // 카카오 오류 본문은 사람에게 보여 줄 글이 아니다 — 우리 문장으로 바꾼다.
        setError('주소를 찾지 못했습니다. 잠시 후 다시 시도해 주세요.')
        setResults([])
        return
      }

      setResults(rows)
    } catch {
      setError('주소 검색에 연결하지 못했습니다. 네트워크를 확인해 주세요.')
      setResults([])
    } finally {
      setLoading(false)
      setHasSearched(true)
    }
  }, [])

  const resolve = useCallback(async (c: AddressCandidate): Promise<AddressCandidate> => {
    if (c.regionCode.length > 0) return c

    const key = readKakaoRestKey()
    if (key === null) return c
    try {
      const url = `${REGION_ENDPOINT}?x=${c.lng}&y=${c.lat}`
      const res = await fetch(url, { headers: { Authorization: `KakaoAK ${key}` } })
      if (!res.ok) return c
      const body = (await res.json()) as {
        documents?: Array<{ region_type: string; code: string; region_3depth_name: string }>
      }
      const docs = body.documents ?? []
      const h = docs.find((d) => d.region_type === 'H')
      if (h === undefined) return c
      // 법정동도 이 응답에 함께 온다(`B`) — 장소 결과에는 없던 값이라 여기서 채운다.
      const b = docs.find((d) => d.region_type === 'B')
      return {
        ...c,
        regionCode: h.code,
        regionName: h.region_3depth_name,
        ...(c.legalCode.length === 0 && b !== undefined
          ? { legalCode: b.code, legalName: b.region_3depth_name }
          : {}),
      }
    } catch {
      // 되묻지 못하면 코드 없는 채로 돌려준다 — 부르는 쪽이 「고를 수 없다」로 다룬다.
      return c
    }
  }, [])

  return { results, isLoading, error, hasSearched, search, resolve, clear }
}
